/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateOutgoingPaymentRequest } from '../models/create-outgoing-payment-request';
import { OutgoingPayment } from '../models/outgoing-payment';
import { OutgoingPaymentReason } from '../models/outgoing-payment-reason';
import { OutgoingPaymentsResponsePage } from '../models/outgoing-payments-response-page';


/**
 * API for managing outgoing bank payments.
 */
@Injectable({
  providedIn: 'root',
})
export class OutgoingPaymentsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listOutgoingPayments
   */
  static readonly ListOutgoingPaymentsPath = '/invoices/{id}/outgoing-payments';

  /**
   * List outgoing payments for an invoice.
   *
   * Lists all outgoing payments associated with an invoice.
   * This includes both new payments and legacy payments from the PaymentTransfers table.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listOutgoingPayments()` instead.
   *
   * This method doesn't expect any request body.
   */
  listOutgoingPayments$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<OutgoingPayment>>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.ListOutgoingPaymentsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<OutgoingPayment>>;
      })
    );
  }

  /**
   * List outgoing payments for an invoice.
   *
   * Lists all outgoing payments associated with an invoice.
   * This includes both new payments and legacy payments from the PaymentTransfers table.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listOutgoingPayments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listOutgoingPayments(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<OutgoingPayment>> {

    return this.listOutgoingPayments$Response(params).pipe(
      map((r: StrictHttpResponse<Array<OutgoingPayment>>) => r.body as Array<OutgoingPayment>)
    );
  }

  /**
   * Path part for operation createOutgoingPayment
   */
  static readonly CreateOutgoingPaymentPath = '/invoices/{id}/outgoing-payments';

  /**
   * Create a new outgoing payment for an invoice.
   *
   * Creates a new outgoing bank payment for a specific invoice.
   * Requires the `payments:create` permission (403 otherwise).
   *
   * `error_code` values returned with `422` include:
   *
   * - `financing_on_hold` — `reason_key` is `INVOICE_FINANCING` or
   *   `FINAL_PAYMENT` and at least one active financing hold applies to
   *   the invoice. No payment is recorded and no bank workflow starts.
   *   The hold is checked before the IBAN and amount verification, so
   *   `allow_amount_override` never bypasses it. `details.holds` is an
   *   array of `FinancingHold`: the active holds that apply, the same
   *   list as the invoice's `financing_holds`. Payments with any other
   *   reason are not checked against holds.
   * - `express_mismatch` — `reason_key` is `INVOICE_FINANCING` and
   *   `is_express` differs from the invoice's `express_payment`: a
   *   financing payout is sent express exactly when the invoice's express
   *   payment is on, since `express_payment_fee` is deducted from
   *   `amount_financed` exactly then. No payment is recorded and no bank
   *   workflow starts. The message tells the caller to change express
   *   payment on the invoice; `details.express_payment` is the invoice's
   *   `express_payment`. `allow_amount_override` does not bypass the
   *   check. Final payments and payments with any other reason are not
   *   checked.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createOutgoingPayment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOutgoingPayment$Response(params: {
    id: number;
    context?: HttpContext
    body: CreateOutgoingPaymentRequest
  }
): Observable<StrictHttpResponse<OutgoingPayment>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.CreateOutgoingPaymentPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OutgoingPayment>;
      })
    );
  }

  /**
   * Create a new outgoing payment for an invoice.
   *
   * Creates a new outgoing bank payment for a specific invoice.
   * Requires the `payments:create` permission (403 otherwise).
   *
   * `error_code` values returned with `422` include:
   *
   * - `financing_on_hold` — `reason_key` is `INVOICE_FINANCING` or
   *   `FINAL_PAYMENT` and at least one active financing hold applies to
   *   the invoice. No payment is recorded and no bank workflow starts.
   *   The hold is checked before the IBAN and amount verification, so
   *   `allow_amount_override` never bypasses it. `details.holds` is an
   *   array of `FinancingHold`: the active holds that apply, the same
   *   list as the invoice's `financing_holds`. Payments with any other
   *   reason are not checked against holds.
   * - `express_mismatch` — `reason_key` is `INVOICE_FINANCING` and
   *   `is_express` differs from the invoice's `express_payment`: a
   *   financing payout is sent express exactly when the invoice's express
   *   payment is on, since `express_payment_fee` is deducted from
   *   `amount_financed` exactly then. No payment is recorded and no bank
   *   workflow starts. The message tells the caller to change express
   *   payment on the invoice; `details.express_payment` is the invoice's
   *   `express_payment`. `allow_amount_override` does not bypass the
   *   check. Final payments and payments with any other reason are not
   *   checked.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createOutgoingPayment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOutgoingPayment(params: {
    id: number;
    context?: HttpContext
    body: CreateOutgoingPaymentRequest
  }
): Observable<OutgoingPayment> {

    return this.createOutgoingPayment$Response(params).pipe(
      map((r: StrictHttpResponse<OutgoingPayment>) => r.body as OutgoingPayment)
    );
  }

  /**
   * Path part for operation cancelOutgoingPayment
   */
  static readonly CancelOutgoingPaymentPath = '/invoices/{invoice_id}/outgoing-payments/{payment_id}/cancellations';

  /**
   * Cancel an outgoing payment submission.
   *
   * Sends a signal to cancel an outgoing payment that has not yet been processed by the bank.
   * This is only possible for a short period after creation. This action is not available for legacy payments.
   * Requires the `payments:cancel` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `cancelOutgoingPayment()` instead.
   *
   * This method doesn't expect any request body.
   */
  cancelOutgoingPayment$Response(params: {
    invoice_id: number;
    payment_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.CancelOutgoingPaymentPath, 'post');
    if (params) {
      rb.path('invoice_id', params.invoice_id, {});
      rb.path('payment_id', params.payment_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Cancel an outgoing payment submission.
   *
   * Sends a signal to cancel an outgoing payment that has not yet been processed by the bank.
   * This is only possible for a short period after creation. This action is not available for legacy payments.
   * Requires the `payments:cancel` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `cancelOutgoingPayment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  cancelOutgoingPayment(params: {
    invoice_id: number;
    payment_id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.cancelOutgoingPayment$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation retryOutgoingPayment
   */
  static readonly RetryOutgoingPaymentPath = '/invoices/{invoice_id}/outgoing-payments/{payment_id}/retries';

  /**
   * Retry a failed outgoing payment submission.
   *
   * Sends a signal to retry submitting an outgoing payment that previously failed.
   * This action is not available for legacy payments.
   * Requires the `payments:retry` permission (403 otherwise).
   *
   * `error_code` values returned with `422`:
   *
   * - `financing_on_hold` — the payment's reason is
   *   `INVOICE_FINANCING` or `FINAL_PAYMENT` and at least one active
   *   financing hold applies to its invoice. No signal is sent and the
   *   payment's status is unchanged. `details.holds` is an array of
   *   `FinancingHold`: the active holds that apply, the same list as
   *   the invoice's `financing_holds`.
   * - `express_mismatch` — the payment's reason is `INVOICE_FINANCING` and
   *   its `is_express` differs from its invoice's `express_payment`, which
   *   can have changed since the payout was created. No signal is sent and
   *   the payment's status is unchanged. The message and
   *   `details.express_payment` are those of `createOutgoingPayment`'s
   *   `express_mismatch`; to send the payout the way the invoice is priced,
   *   create a new one. Final payments and payments with any other reason
   *   are not checked.
   *
   * A `422` without an `error_code` refuses an express payment whose
   * recipient's bank is not on the express allowlist, as creating it
   * would.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `retryOutgoingPayment()` instead.
   *
   * This method doesn't expect any request body.
   */
  retryOutgoingPayment$Response(params: {
    invoice_id: number;
    payment_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.RetryOutgoingPaymentPath, 'post');
    if (params) {
      rb.path('invoice_id', params.invoice_id, {});
      rb.path('payment_id', params.payment_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Retry a failed outgoing payment submission.
   *
   * Sends a signal to retry submitting an outgoing payment that previously failed.
   * This action is not available for legacy payments.
   * Requires the `payments:retry` permission (403 otherwise).
   *
   * `error_code` values returned with `422`:
   *
   * - `financing_on_hold` — the payment's reason is
   *   `INVOICE_FINANCING` or `FINAL_PAYMENT` and at least one active
   *   financing hold applies to its invoice. No signal is sent and the
   *   payment's status is unchanged. `details.holds` is an array of
   *   `FinancingHold`: the active holds that apply, the same list as
   *   the invoice's `financing_holds`.
   * - `express_mismatch` — the payment's reason is `INVOICE_FINANCING` and
   *   its `is_express` differs from its invoice's `express_payment`, which
   *   can have changed since the payout was created. No signal is sent and
   *   the payment's status is unchanged. The message and
   *   `details.express_payment` are those of `createOutgoingPayment`'s
   *   `express_mismatch`; to send the payout the way the invoice is priced,
   *   create a new one. Final payments and payments with any other reason
   *   are not checked.
   *
   * A `422` without an `error_code` refuses an express payment whose
   * recipient's bank is not on the express allowlist, as creating it
   * would.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `retryOutgoingPayment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  retryOutgoingPayment(params: {
    invoice_id: number;
    payment_id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.retryOutgoingPayment$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation resubmitOutgoingPayment
   */
  static readonly ResubmitOutgoingPaymentPath = '/invoices/{invoice_id}/outgoing-payments/{payment_id}/resubmissions';

  /**
   * Resubmit an outgoing payment.
   *
   * Reverts a payment from a 'SUBMITTED_TO_BANK' state back to 'PENDING_SUBMISSION' and starts a new submission workflow.
   * This is intended for cases where a payment is stuck and suspected to have not been processed by the bank.
   * This action is not available for legacy payments.
   * Requires the `payments:resubmit` permission (403 otherwise).
   *
   * `error_code` values returned with `422`:
   *
   * - `financing_on_hold` — the payment's reason is
   *   `INVOICE_FINANCING` or `FINAL_PAYMENT` and at least one active
   *   financing hold applies to its invoice. The payment stays in
   *   `SUBMITTED_TO_BANK` and no submission workflow starts.
   *   `details.holds` is an array of `FinancingHold`: the active holds
   *   that apply, the same list as the invoice's `financing_holds`.
   * - `express_mismatch` — the payment's reason is `INVOICE_FINANCING` and
   *   its `is_express` differs from its invoice's `express_payment`, which
   *   can have changed since the payout was created. The payment stays in
   *   `SUBMITTED_TO_BANK` and no submission workflow starts. The message and
   *   `details.express_payment` are those of `createOutgoingPayment`'s
   *   `express_mismatch`; to send the payout the way the invoice is priced,
   *   create a new one. Final payments and payments with any other reason
   *   are not checked.
   *
   * A `422` without an `error_code` refuses an express payment whose
   * recipient's bank is not on the express allowlist, as creating it
   * would.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resubmitOutgoingPayment()` instead.
   *
   * This method doesn't expect any request body.
   */
  resubmitOutgoingPayment$Response(params: {
    invoice_id: number;
    payment_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.ResubmitOutgoingPaymentPath, 'post');
    if (params) {
      rb.path('invoice_id', params.invoice_id, {});
      rb.path('payment_id', params.payment_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Resubmit an outgoing payment.
   *
   * Reverts a payment from a 'SUBMITTED_TO_BANK' state back to 'PENDING_SUBMISSION' and starts a new submission workflow.
   * This is intended for cases where a payment is stuck and suspected to have not been processed by the bank.
   * This action is not available for legacy payments.
   * Requires the `payments:resubmit` permission (403 otherwise).
   *
   * `error_code` values returned with `422`:
   *
   * - `financing_on_hold` — the payment's reason is
   *   `INVOICE_FINANCING` or `FINAL_PAYMENT` and at least one active
   *   financing hold applies to its invoice. The payment stays in
   *   `SUBMITTED_TO_BANK` and no submission workflow starts.
   *   `details.holds` is an array of `FinancingHold`: the active holds
   *   that apply, the same list as the invoice's `financing_holds`.
   * - `express_mismatch` — the payment's reason is `INVOICE_FINANCING` and
   *   its `is_express` differs from its invoice's `express_payment`, which
   *   can have changed since the payout was created. The payment stays in
   *   `SUBMITTED_TO_BANK` and no submission workflow starts. The message and
   *   `details.express_payment` are those of `createOutgoingPayment`'s
   *   `express_mismatch`; to send the payout the way the invoice is priced,
   *   create a new one. Final payments and payments with any other reason
   *   are not checked.
   *
   * A `422` without an `error_code` refuses an express payment whose
   * recipient's bank is not on the express allowlist, as creating it
   * would.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `resubmitOutgoingPayment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  resubmitOutgoingPayment(params: {
    invoice_id: number;
    payment_id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.resubmitOutgoingPayment$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listOutgoingPaymentReasons
   */
  static readonly ListOutgoingPaymentReasonsPath = '/outgoing-payment-reasons';

  /**
   * List all available outgoing payment reasons.
   *
   * Lists all available reasons that can be used when creating an outgoing payment.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listOutgoingPaymentReasons()` instead.
   *
   * This method doesn't expect any request body.
   */
  listOutgoingPaymentReasons$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<OutgoingPaymentReason>>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.ListOutgoingPaymentReasonsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<OutgoingPaymentReason>>;
      })
    );
  }

  /**
   * List all available outgoing payment reasons.
   *
   * Lists all available reasons that can be used when creating an outgoing payment.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listOutgoingPaymentReasons$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listOutgoingPaymentReasons(params?: {
    context?: HttpContext
  }
): Observable<Array<OutgoingPaymentReason>> {

    return this.listOutgoingPaymentReasons$Response(params).pipe(
      map((r: StrictHttpResponse<Array<OutgoingPaymentReason>>) => r.body as Array<OutgoingPaymentReason>)
    );
  }

  /**
   * Path part for operation listAllOutgoingPayments
   */
  static readonly ListAllOutgoingPaymentsPath = '/outgoing-payments';

  /**
   * List all outgoing payments.
   *
   * Returns a paginated list of all outgoing payments, with optional filtering.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listAllOutgoingPayments()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllOutgoingPayments$Response(params?: {

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;

    /**
     * Filter by payment status (PENDING, IN_PROGRESS, SUCCESS, FAILED)
     */
    status?: Array<'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED'>;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<OutgoingPaymentsResponsePage>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.ListAllOutgoingPaymentsPath, 'get');
    if (params) {
      rb.query('limit', params.limit, {});
      rb.query('after', params.after, {});
      rb.query('status', params.status, {"style":"form","explode":false});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OutgoingPaymentsResponsePage>;
      })
    );
  }

  /**
   * List all outgoing payments.
   *
   * Returns a paginated list of all outgoing payments, with optional filtering.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listAllOutgoingPayments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllOutgoingPayments(params?: {

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;

    /**
     * Filter by payment status (PENDING, IN_PROGRESS, SUCCESS, FAILED)
     */
    status?: Array<'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED'>;
    context?: HttpContext
  }
): Observable<OutgoingPaymentsResponsePage> {

    return this.listAllOutgoingPayments$Response(params).pipe(
      map((r: StrictHttpResponse<OutgoingPaymentsResponsePage>) => r.body as OutgoingPaymentsResponsePage)
    );
  }

  /**
   * Path part for operation pollOutgoingPaymentResponses
   */
  static readonly PollOutgoingPaymentResponsesPath = '/outgoing-payments/actions/poll-responses';

  /**
   * Trigger an immediate bank-response poll.
   *
   * Signals the singleton bank-payment-response-poller Temporal workflow to run an immediate poll for bank payment responses, instead of waiting for its next scheduled (business-hours-gated) tick. Requires the payments:poll-responses permission. The poll runs asynchronously: a 204 confirms the signal was delivered, not that the poll completed or that any responses were found.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `pollOutgoingPaymentResponses()` instead.
   *
   * This method doesn't expect any request body.
   */
  pollOutgoingPaymentResponses$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OutgoingPaymentsService.PollOutgoingPaymentResponsesPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Trigger an immediate bank-response poll.
   *
   * Signals the singleton bank-payment-response-poller Temporal workflow to run an immediate poll for bank payment responses, instead of waiting for its next scheduled (business-hours-gated) tick. Requires the payments:poll-responses permission. The poll runs asynchronously: a 204 confirms the signal was delivered, not that the poll completed or that any responses were found.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `pollOutgoingPaymentResponses$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  pollOutgoingPaymentResponses(params?: {
    context?: HttpContext
  }
): Observable<void> {

    return this.pollOutgoingPaymentResponses$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
